import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
// Import MUI components
import {
  Drawer,
  List,
  Typography,
  Box,
  useTheme,
  IconButton,
  Stack,
  Divider,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import * as CONFIG from '@/config'
import { getCurrentOrganisation } from '@/store/agents/actions'
import { getCommonState } from '@/store/common/selector'
import { CustomLink } from './CustomLink'
import { Dashboard, Knowledge, Question, Quiz, Star } from '../UI/Icons'
import CommonService from '@/services/CommonService'
import Logo from '@/assets/New-images/logo.svg'
import logoHead from '@/assets/images/logo-head.svg'

const commonService = new CommonService()

interface SidebarItem {
  icon: ReactNode
  label: string
  link: string
}

interface SidebarProps {
  isCollapsed: boolean
  toggleCollapse: () => void
}

const Sidebar: FC<SidebarProps> = ({ isCollapsed, toggleCollapse }) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const theme = useTheme()

  const [showPayment, setShowPayment] = useState(false)
  const [showsIntegration, setShowsIntegration] = useState(true)

  const commonState = useSelector(getCommonState)
  const isSuperAdmin = commonState?.user?.role === 'superadmin'

  const func = async () => {
    try {
      const currentOrganisation: any = await dispatch(getCurrentOrganisation())
      // console.log("🚀 ~ func ~ currentOrganisation:", currentOrganisation);
      if (currentOrganisation) {
        if (
          !['ultimate', 'institution'].includes(currentOrganisation?.tier?.tier)
        ) {
          setShowPayment(true)
        }
      }
    } catch (error) {}
  }

  const getOrganisationFeatures = async () => {
    const { status, data } = await commonService.getOrganisationFeatures()
    if (status === 200) {
      const learning_lti_feature = data?.data?.features.find(
        (feature: any) => feature.name === 'learning_lti',
      )
      if (!learning_lti_feature) {
        setShowsIntegration(false)
      }
    }
  }

  useEffect(() => {
    func()

    getOrganisationFeatures()
  }, [])

  const categories: {
    category: string
    items: SidebarItem[]
  }[] = [
    {
      category: '#',
      items: [
        {
          icon: <Dashboard />,
          label: t('Dashboard'),
          link: CONFIG.ORGANISATION_DASHBOARD,
        },
      ],
    },
    {
      category: t('Configure'),
      items: [
        ...(showsIntegration && isSuperAdmin
          ? [
              {
                icon: <Knowledge />,
                label: t('Integrations'),
                link: CONFIG.INTEGRATION_MANAGEMENT,
              },
            ]
          : []),
        ...(showPayment
          ? [
              {
                icon: <Quiz />,
                label: t('Subscription'),
                link: CONFIG.PLAN_MANAGEMENT,
              },
            ]
          : []),
      ],
    },
  ]

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 200,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        onClick={toggleCollapse}
        sx={{
          position: 'absolute',
          // display: { xs: 'none', md: 'flex' },
          display: 'flex',
          right: -15,
          top: 20,
          backgroundColor: 'white',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #eaeaea',
          width: 30,
          height: 30,
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          zIndex: 9999,
        }}
      >
        {isCollapsed ? (
          <ChevronRightIcon fontSize="small" />
        ) : (
          <ChevronLeftIcon fontSize="small" />
        )}
      </IconButton>

      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
        }}
      >
        <img
          src={isCollapsed ? logoHead : Logo}
          alt="Logo"
          style={{ maxWidth: isCollapsed ? '40px' : '100%' }}
        />
      </Box>

      <Stack flexGrow={1} sx={{ overflow: 'auto', gap: 2 }}>
        {categories.map(({ category, items }, categoryIndex) =>
          category === '#' ? (
            <List key={categoryIndex} disablePadding>
              {items.map((item, index) => (
                <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
              ))}
            </List>
          ) : (
            <Box key={categoryIndex}>
              {!isCollapsed && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    color: '#79767A',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {category}
                </Typography>
              )}
              <List disablePadding sx={{ mt: 0.5 }}>
                {items.map((item, index) => (
                  <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
                ))}
              </List>
            </Box>
          ),
        )}
      </Stack>

      <Divider sx={{ pt: 2 }} />

      <Box sx={{ py: 2 }}>
        <CustomLink
          icon={<Star />}
          label={t("What's New")}
          link="https://noodlefactory-support.notion.site/New-Releases-247a6979da7e80a3a505e9189a3b1e6a"
          isExternal
          isCollapsed={isCollapsed}
        />
        <CustomLink
          icon={<Question />}
          label={t('Support')}
          link={CONFIG.ORGANISATION_SUPPORT}
          isCollapsed={isCollapsed}
        />
      </Box>
    </Box>
  )
}

interface CustomSidebarProps {
  isCollapsed: boolean
  toggleCollapse: () => void
}

const OrganisationSidebar: FC<CustomSidebarProps> = ({
  isCollapsed,
  toggleCollapse,
}) => {
  const theme = useTheme()

  return (
    <>
      {/* Desktop Drawer - persistent */}
      <Drawer
        variant="permanent"
        sx={{
          // display: { xs: 'none', md: 'block' },
          display: 'block',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 70 : 200,
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            border: 'none',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: 'visible',
          },
        }}
        open
      >
        <Sidebar
          isCollapsed={isCollapsed}
          toggleCollapse={() => {
            toggleCollapse()
          }}
        />
      </Drawer>
    </>
  )
}

export default OrganisationSidebar
